<template v-if="(missionPlan && missionPlan.mission_points) || viewCurrentMissionPlan">
    <template v-for="(poly, idx) in polygonLatLngs" :key="'poly-' + idx">
      <l-polyline v-if="poly.length > 1" :lat-lngs="poly" :color="missionColor" :weight="3"
        :opacity="0.8" :dash-array="'4, 4'" />
    </template>
    <template v-for="point in missionPlan?.mission_points" :key="point.plan_point_id">
        <!-- Marcador Cuadrado para cada punto de misión -->
        <SquareMissionMarker v-if="point.type == '0'" :point="point" :color="missionColor" />
        <!-- Marcador Círculo para punto tipo area -->
        <l-marker v-if="point.type == '2'" :lat-lng="getLatLng(point)"
            :icon="getCircleIcon(point.num, clearNill(point.name) ?? undefined) as unknown as L.Icon<L.IconOptions>">
            <l-tooltip>
                <div>
                    <div v-if="point.obs" class="obs-tooltip"><strong>{{ point.obs }}</strong></div>
                    <div v-if="point.name">{{ point.name }}</div>
                    <div v-if="point.location">
                        <small>
                            {{
                                (() => {
                                    try {
                                        const loc = typeof point.location === 'string' ? JSON.parse(point.location) : point.location
                                        return `(${loc.coordinates[1].toFixed(6)}, ${loc.coordinates[0].toFixed(6)})`
                                    } catch {
                                        return ''
                                    }
                                })()
                            }}
                        </small>
                    </div>
                </div>
            </l-tooltip>
        </l-marker>
        <l-circle v-if="point.type == '2' && point.size && !isNaN(Number(point.size))" :lat-lng="getLatLng(point)"
            :radius="Number(point.size) * 1000" :weight="1" :fill-opacity="0.08" :color="missionColor"
            :fill-color="missionColor" />
        <!-- Marcador Hexágono para punto tipo predio -->
        <l-marker v-else-if="point.type == '1'" :lat-lng="getLatLng(point)"
            :icon="getHexagonIcon(point.num, point.name ?? undefined) as unknown as L.Icon<L.IconOptions>">
            <l-tooltip>
                <div>
                    <div v-if="point.obs" class="obs-tooltip"><strong>{{ point.obs }}</strong></div>
                    <div v-if="point.name">{{ point.name }}</div>
                    <div v-if="point.location">
                        <small>
                            {{
                                (() => {
                                    try {
                                        const loc = typeof point.location === 'string' ? JSON.parse(point.location) : point.location
                                        return `(${loc.coordinates[1].toFixed(6)}, ${loc.coordinates[0].toFixed(6)})`
                                    } catch {
                                        return ''
                                    }
                                })()
                            }}
                        </small>
                    </div>
                </div>
            </l-tooltip>
        </l-marker>
        <!-- Si el tipo es 1 y tiene predio_geom -->
        <template v-if="point.type == '1' && typeof point.predio_geom === 'string' && point.predio_geom">
            <l-geo-json :geojson="getGeo(point.predio_geom)" :options-style="getLayerStyle('predio')"></l-geo-json>
        </template>
    </template>

</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useGlobalStore } from '@/stores/global'
import { LPolyline, LMarker, LTooltip, LCircle, LGeoJson } from "@vue-leaflet/vue-leaflet"
import { defineProps } from 'vue'
import L from 'leaflet'
import { storeToRefs } from 'pinia'
import SquareMissionMarker from './missions/SquareMissionMarker.vue'
import { useUtils } from '@/composables/useUtils'
const { clearNill, intToHexColor } = useUtils()

// Register Vue Leaflet components for use in the template
defineProps({})


// Accede a la variable missionPoints del global store
const globalStore = useGlobalStore()

const { missionPlan, viewCurrentMissionPlan } = storeToRefs(globalStore);
// Convierte los puntos de misión a un array de [lat, lng]

const missionColor = ref('#ff9800') // Color para las líneas de misión

watch(
    () => missionPlan.value?.color,
    (newColor) => {
        if (newColor) {
            missionColor.value = intToHexColor(newColor)
        }
    },
    { immediate: true }
)
const polygonLatLngs = computed(() => {
    if (!missionPlan.value || !Array.isArray(missionPlan.value.mission_points)) return [];
    const polygons: [number, number][][] = [];
    let current: [number, number][] = [];
    for (const point of missionPlan.value.mission_points) {
        try {
            const loc = typeof point.location === 'string' ? JSON.parse(point.location) : point.location;
            if (
                loc &&
                Array.isArray(loc.coordinates) &&
                typeof loc.coordinates[0] === 'number' &&
                typeof loc.coordinates[1] === 'number'
            ) {
                current.push([loc.coordinates[1], loc.coordinates[0]]);
                if (String(point.type) === '4') {
                    if (current.length > 0) polygons.push(current);
                    current = [];
                }
            }
        } catch {
            // Ignorar errores de parseo
        }
    }
    if (current.length > 0) polygons.push(current);
    return polygons.filter(poly => poly.length > 1);
});
// Función para obtener [lat, lng] de un punto
function getLatLng(point: any): [number, number] {
    try {
        const loc = typeof point.location === 'string' ? JSON.parse(point.location) : point.location
        if (
            loc &&
            Array.isArray(loc.coordinates) &&
            typeof loc.coordinates[0] === 'number' &&
            typeof loc.coordinates[1] === 'number'
        ) {
            return [loc.coordinates[1], loc.coordinates[0]]
        }
        return [0, 0]
    } catch {
        return [0, 0]
    }
}

// Icono cuadrado con el número dentro
function getSquareIcon(num: string, name?: string) {
    const key = `square-${num}-${name || ''}`
    if (iconCache.has(key)) return iconCache.get(key)!
    const icon = L.divIcon({
        className: 'mission-square-icon',
        html: `
      <div style="position: relative; width: 28px; height: 28px;">
        <svg width="28" height="28" viewBox="0 0 28 28">
          <rect x="3" y="3" width="22" height="22" rx="4" fill="#fff" stroke="${missionColor.value}" stroke-width="2"/>
          <text x="14" y="19" text-anchor="middle" font-size="13" font-family="Arial" font-weight="bold" fill="${missionColor.value}">${num}</text>
        </svg>
        ${name
                ? `<div style="
                  position: absolute;
                  left: 100%;
                  bottom: 0;
                  background:  ${missionColor.value};
                  color: #fff;
                  font-size: 10px;
                  font-family: Arial, sans-serif;
                  font-weight: normal;
                  padding: 3px 10px 3px 10px;
                  border-radius: 4px;
                  margin-left: 4px;
                  white-space: nowrap;
                  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
                  line-height: 1.2;
                  display: inline-block;
                  opacity: 0.7;
                ">${name}</div>`
                : ''
            }
      </div>
    `,
        iconSize: [28, 28],
        iconAnchor: [14, 14]
    })
    iconCache.set(key, icon)
    return icon
}
const iconCache = new Map<string, L.DivIcon>()
function getCircleIcon(num: string, name?: string) {
    const key = `circle-${num}-${name || ''}`
    if (iconCache.has(key)) return iconCache.get(key)!
    const icon = L.divIcon({
        className: 'mission-circle-icon',
        html: `
      <div style="position: relative; width: 28px; height: 28px;">
        <svg width="28" height="28" viewBox="0 0 28 28">
          <circle cx="14" cy="14" r="12" fill="#fff" stroke="${missionColor.value}" stroke-width="2"/>
          <text x="14" y="19" text-anchor="middle" font-size="13" font-family="Arial" font-weight="bold" fill="${missionColor.value}">${num}</text>
        </svg>
        ${name
                ? `<div style="
                  position: absolute;
                  left: 100%;
                  bottom: 0;
                  background:  ${missionColor.value};
                  color: #fff;
                  font-size: 10px;
                  font-family: Arial, sans-serif;
                  font-weight: normal;
                  padding: 3px 10px 3px 10px;
                  border-radius: 4px;
                  margin-left: 4px;
                  white-space: nowrap;
                  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
                  line-height: 1.2;
                  display: inline-block;
                  opacity: 0.7;
                ">${name}</div>`
                : ''
            }
      </div>
    `,
        iconSize: [28, 28],
        iconAnchor: [14, 14]
    })
    iconCache.set(key, icon)
    return icon
}
// Cache de íconos hexagonales
function getHexagonIcon(num: string, name?: string) {
    const key = `hexagon-${num}-${name || ''}`
    if (iconCache.has(key)) return iconCache.get(key)!
    const icon = L.divIcon({
        className: 'mission-hexagon-icon',
        html: `
      <div style="position: relative; width: 37px; height: 37px;">
        <svg width="37" height="37" viewBox="0 0 37 37">
          <polygon 
            points="18.5,4 33,12.5 33,24.5 18.5,33 4,24.5 4,12.5"
            fill="#fff"
            stroke="${missionColor.value}"
            stroke-width="2"
          />
          <text x="18.5" y="24" text-anchor="middle" font-size="17" font-family="Arial" font-weight="bold" fill="${missionColor.value}">${num}</text>
        </svg>
        ${name
                ? `<div style="
                  position: absolute;
                  left: 100%;
                  bottom: 0;
                  background: ${missionColor.value};
                  color: #fff;
                  font-size: 10px;
                  font-family: Arial, sans-serif;
                  font-weight: normal;
                  padding: 3px 10px 3px 10px;
                  border-radius: 4px;
                  margin-left: 4px;
                  white-space: nowrap;
                  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
                  line-height: 1.2;
                  display: inline-block;
                  opacity: 0.7;
                ">${name}</div>`
                : ''
            }
      </div>
    `,
        iconSize: [37, 37],
        iconAnchor: [18.5, 18.5]
    })
    iconCache.set(key, icon)
    return icon
}
function getLayerStyle(type: string) {
    return (feature?: any) => {
        switch (type) {
            case 'camPolygon':
                return { color: "#6b9fda", weight: 1, fillOpacity: 0.3 };
            case 'predios':
                return { color: "green", weight: 1, fillOpacity: 0.3 };
            case 'regiones':
                return { color: "#0000ff", weight: 2, fillOpacity: 0.3 };
            case 'comunas':
                return { color: "#0000ff", weight: 1, fillOpacity: 0.3 };
            case 'ciudades':
                return { color: "green", weight: 1, fillOpacity: 0.3 };
            case 'carreteras':
                return { color: "red", weight: 1, fillOpacity: 0.3 };
            case 'predio':
                return { color: missionColor.value, weight: 1, fillOpacity: 0.3 };
            default:
                return { color: "#888", weight: 1, fillOpacity: 0.2 };
        }
    };
}
function getGeo(geo: string | any) {
    const cleaned = geo.replace(/\\"/g, '"')
    const geojson = JSON.parse(cleaned);
    return geojson
}
</script>


<style>
.mission-square {
    width: 28px;
    height: 28px;
    background: #fff;
    border: 2px solid #ff9800;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: #ff9800;
    font-size: 16px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.mission-circle {
    width: 28px;
    height: 28px;
    background: #fff;
    border: 2px solid #ff9800;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: #ff9800;
    font-size: 16px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.obs-tooltip {
    max-width: 220px;
    /* Ajusta el ancho máximo según tu preferencia */
    white-space: pre-line;
    /* Respeta saltos de línea y permite el wrap automático */
    word-break: break-word;
}
</style>